// src/GoToDate.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: { paper: "#121212" },
    text: { primary: "#ffffff", secondary: "#BBBBBB" },
    primary: { main: "#d4af37" },
  },
});

const STORAGE_KEY = "GoToDate:lastSelectedSession";

const MARKER_ROOT_STYLE = {
  position: "absolute",
  inset: "0",
  pointerEvents: "none",
  zIndex: 2147483647,
};

const MARKER_LABEL_STYLE = {
  position: "absolute",
  top: "14px",
  left: "50%",
  transform: "translateX(-50%)",
  background: "linear-gradient(180deg, rgba(31,35,46,0.96), rgba(14,17,24,0.96))",
  color: "#f7e7a1",
  padding: "7px 10px",
  borderRadius: "7px",
  border: "1px solid rgba(212,175,55,0.46)",
  fontFamily: "monospace",
  fontSize: "12px",
  lineHeight: "1",
  letterSpacing: "0",
  whiteSpace: "nowrap",
  boxShadow: "0 10px 28px rgba(0,0,0,0.42)",
};

const calendarWrapperSx = {
  "& .MuiCalendarPicker-root": { bgcolor: "background.paper", color: "text.primary", borderRadius: 1, p: 1 },
  "& .MuiPickersCalendarHeader-label": { color: "#ffffff", fontWeight: 600, fontSize: "1rem" },
  "& .MuiPickersCalendarHeader-iconButton": { color: "#ffffff", opacity: 0.9 },
  "& .MuiDayCalendar-weekDayLabel": { color: "#cfcfcf" },
  "& .MuiPickersDay-root": { color: "#e6e6e6" },
  "& .Mui-selected": { bgcolor: "primary.main !important", color: "#111827 !important", fontWeight: 700 },
  "& .MuiPickersDay-today": { borderColor: "rgba(255,255,255,0.12)" },
  "& .Mui-disabled": { color: "rgba(255,255,255,0.12) !important" },
  "& .MuiYearCalendar-root .MuiPickersYear-root:not(.Mui-disabled)": { color: "#ffffff", fontWeight: 500 },
  "& .MuiYearCalendar-root .MuiPickersYear-root.Mui-disabled": { color: "rgba(255,255,255,0.38) !important" },
  "& .MuiMonthCalendar-root .MuiPickersMonth-root:not(.Mui-disabled)": { color: "#ffffff" },
  "& .MuiMonthCalendar-root .MuiPickersMonth-root.Mui-disabled": { color: "rgba(255,255,255,0.38) !important" },
};

const pad2 = (value) => String(value).padStart(2, "0");

const dateToDayKey = (date) => {
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const candleToDayKey = (candle) => {
  if (!candle) return null;
  const seconds = Number(candle.time);
  if (Number.isFinite(seconds)) {
    const d = new Date(seconds * 1000);
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  }
  if (candle.ts) {
    const m = String(candle.ts).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  return null;
};

const dayKeyToDate = (key) => {
  if (!key) return null;
  const m = String(key).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const parseDateParam = (value) => {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getCandleSeconds = (candle) => {
  const seconds = Number(candle?.time);
  if (Number.isFinite(seconds)) return seconds;
  if (candle?.ts) {
    const parsed = Date.parse(String(candle.ts).replace(" ", "T"));
    if (Number.isFinite(parsed)) return Math.floor(parsed / 1000);
  }
  return null;
};

const getLatestAvailableDate = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    const d = dayKeyToDate(candleToDayKey(arr[i]));
    if (d) return d;
  }
  return null;
};

const formatMarkerTime = (seconds) => {
  if (!Number.isFinite(seconds)) return "";
  const d = new Date(seconds * 1000);
  return `${pad2(d.getUTCDate())}/${pad2(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
};

export default function GoToDate({ chart, dataRef, containerRef }) {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const markerRef = useRef(null);
  const visibleSubRef = useRef(null);
  const markerTimerRef = useRef(null);
  const availabilityRef = useRef({ builtForRef: null, builtForLen: -1, sets: null });

  const removeMarker = useCallback(() => {
    if (markerTimerRef.current) {
      clearTimeout(markerTimerRef.current);
      markerTimerRef.current = null;
    }

    const marker = markerRef.current;
    if (marker?.el?.parentNode) {
      try { marker.el.parentNode.removeChild(marker.el); } catch {}
    }
    markerRef.current = null;

    const subscribed = visibleSubRef.current;
    if (subscribed && chart?.timeScale) {
      const timeScale = chart.timeScale();
      try { timeScale.unsubscribeVisibleLogicalRangeChange(subscribed); } catch {}
      try { timeScale.unsubscribeVisibleTimeRangeChange(subscribed); } catch {}
      try { timeScale.unsubscribeSizeChange(subscribed); } catch {}
    }
    visibleSubRef.current = null;
  }, [chart]);

  const buildAvailabilityIfNeeded = useCallback(() => {
    const arr = dataRef?.current ?? [];
    if (
      availabilityRef.current.builtForRef === arr &&
      availabilityRef.current.builtForLen === arr.length &&
      availabilityRef.current.sets
    ) {
      return availabilityRef.current.sets;
    }

    const days = new Set();
    const months = new Set();
    const years = new Set();

    for (let i = 0, len = arr.length; i < len; i += 1) {
      const key = candleToDayKey(arr[i]);
      if (!key) continue;
      const [year, month] = key.split("-");
      days.add(key);
      months.add(`${year}-${month}`);
      years.add(Number(year));
    }

    availabilityRef.current = {
      builtForRef: arr,
      builtForLen: arr.length,
      sets: { availableDays: days, availableMonths: months, availableYears: years },
    };
    return availabilityRef.current.sets;
  }, [dataRef]);

  const findDateIndex = useCallback((arr, dateObj) => {
    if (!Array.isArray(arr) || arr.length === 0 || !dateObj) return -1;
    const targetKey = dateToDayKey(dateObj);
    if (!targetKey) return -1;

    const [year, month, day] = targetKey.split("-").map(Number);
    const targetSeconds = Math.floor(Date.UTC(year, month - 1, day) / 1000);

    let lo = 0;
    let hi = arr.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const seconds = getCandleSeconds(arr[mid]);
      if (seconds == null || seconds < targetSeconds) lo = mid + 1;
      else hi = mid - 1;
    }

    for (let i = Math.max(0, lo - 2); i < arr.length; i += 1) {
      const key = candleToDayKey(arr[i]);
      if (key === targetKey) return i;
      if (key && key > targetKey) break;
    }

    let best = -1;
    let bestDiff = Infinity;
    for (let i = Math.max(0, lo - 3), end = Math.min(arr.length - 1, lo + 3); i <= end; i += 1) {
      const seconds = getCandleSeconds(arr[i]);
      if (seconds == null) continue;
      const diff = Math.abs(seconds - targetSeconds);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    return best;
  }, []);

  const ensurePriceScaleFits = useCallback(() => {
    if (!chart || !dataRef?.current) return;
    const tick = () => {
      try {
        const arr = dataRef.current;
        if (!arr?.length) return;

        const vlr = chart.timeScale?.()?.getVisibleLogicalRange?.();
        let fromIdx = 0;
        let toIdx = arr.length - 1;
        if (vlr && Number.isFinite(Number(vlr.from)) && Number.isFinite(Number(vlr.to))) {
          fromIdx = Math.max(0, Math.floor(Number(vlr.from)));
          toIdx = Math.min(arr.length - 1, Math.ceil(Number(vlr.to)));
        }

        let min = Infinity;
        let max = -Infinity;
        for (let i = fromIdx; i <= toIdx; i += 1) {
          const c = arr[i];
          const low = Number(c?.low);
          const high = Number(c?.high);
          if (!Number.isFinite(low) || !Number.isFinite(high)) continue;
          min = Math.min(min, low);
          max = Math.max(max, high);
        }
        if (!Number.isFinite(min) || !Number.isFinite(max)) return;

        const rawRange = max - min;
        const pad = rawRange > 0 ? Math.max(rawRange * 0.06, Math.abs(max) * 0.0005) : Math.abs(max) * 0.01 || 1;
        const priceScaleApi = chart.priceScale?.("right");
        if (!priceScaleApi) return;

        if (typeof priceScaleApi.setAutoScale === "function") {
          try { priceScaleApi.setAutoScale(true); } catch {}
        } else if (typeof priceScaleApi.applyOptions === "function") {
          try { priceScaleApi.applyOptions({ autoScale: true }); } catch {}
        }

        if (typeof priceScaleApi.setVisibleRange === "function") {
          try { priceScaleApi.setVisibleRange({ from: min - pad, to: max + pad }); } catch {}
        }
      } catch {}
    };

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(tick);
    } else {
      setTimeout(tick, 0);
    }
  }, [chart, dataRef]);

  const createMarker = useCallback((candle) => {
    removeMarker();
    if (!containerRef?.current || !chart || !candle) return;

    const root = containerRef.current;
    const marker = document.createElement("div");
    const label = document.createElement("div");

    Object.assign(marker.style, MARKER_ROOT_STYLE);
    Object.assign(label.style, MARKER_LABEL_STYLE);

    label.textContent = `Selected candle ${formatMarkerTime(getCandleSeconds(candle))}`;
    marker.appendChild(label);
    root.appendChild(marker);
    markerRef.current = { el: marker, label, candle };
    markerTimerRef.current = setTimeout(removeMarker, 5000);
  }, [chart, containerRef, removeMarker]);

  const handleGo = useCallback((date) => {
    if (!chart || !dataRef?.current || !containerRef?.current || !date) return;
    const arr = dataRef.current;
    if (!arr?.length) return;

    const idx = findDateIndex(arr, date);
    if (idx < 0) return;
    const candle = arr[idx];

    let spanIdx = null;
    try {
      const vlr = chart.timeScale().getVisibleLogicalRange?.();
      if (vlr && vlr.from != null && vlr.to != null) spanIdx = Number(vlr.to) - Number(vlr.from);
    } catch {}
    if (!Number.isFinite(spanIdx) || spanIdx <= 2) spanIdx = Math.min(160, Math.max(40, arr.length - 1));

    try {
      const half = spanIdx / 2;
      chart.timeScale().setVisibleLogicalRange?.({ from: idx - half, to: idx + half });
    } catch {}

    createMarker(candle);
    ensurePriceScaleFits();
  }, [chart, dataRef, containerRef, findDateIndex, createMarker, ensurePriceScaleFits]);

  useEffect(() => {
    try {
      let parsed = null;
      const params = new URLSearchParams(window.location.search);
      const gotoDate = params.get("gotoDate");
      if (gotoDate) parsed = parseDateParam(gotoDate);

      if (!parsed) {
        const hs = (typeof window !== "undefined" && window.history && window.history.state) || null;
        if (hs && hs[STORAGE_KEY]) {
          const d = new Date(hs[STORAGE_KEY]);
          if (!Number.isNaN(d.getTime())) parsed = d;
        }
      }

      if (!parsed) {
        const s = sessionStorage.getItem(STORAGE_KEY);
        if (s) {
          const d = new Date(s);
          if (!Number.isNaN(d.getTime())) parsed = d;
        }
      }

      if (parsed) {
        setSelectedDate(parsed);
        sessionStorage.setItem(STORAGE_KEY, parsed.toISOString());
        try {
          const newState = Object.assign({}, window.history.state || {}, { [STORAGE_KEY]: parsed.toISOString() });
          window.history.replaceState(newState, "");
        } catch {}

        if (gotoDate) {
          const timer = setTimeout(() => handleGo(parsed), 0);
          return () => clearTimeout(timer);
        }
      }
    } catch (e) {
      console.warn("Failed to initialize GoToDate", e);
    }
    return undefined;
  }, [handleGo]);

  useEffect(() => {
    const onKey = (e) => {
      try {
        if (e.altKey && e.key && e.key.toLowerCase() === "g") {
          e.preventDefault();
          setOpen(true);
        }
      } catch {}
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) buildAvailabilityIfNeeded();
  }, [open, buildAvailabilityIfNeeded]);

  useEffect(() => () => { removeMarker(); }, [removeMarker]);

  const onCalendarChange = useCallback((newDate) => {
    setOpen(false);
    if (!newDate) return;
    try { sessionStorage.setItem(STORAGE_KEY, newDate.toISOString()); } catch {}
    try {
      const newState = Object.assign({}, window.history.state || {}, { [STORAGE_KEY]: newDate.toISOString() });
      window.history.replaceState(newState, "");
    } catch {}
    setSelectedDate(newDate);
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => handleGo(newDate));
    } else {
      setTimeout(() => handleGo(newDate), 0);
    }
  }, [handleGo]);

  const shouldDisableYear = useCallback((yearDate) => {
    const sets = availabilityRef.current.sets || buildAvailabilityIfNeeded();
    return !sets.availableYears.has(yearDate.getFullYear());
  }, [buildAvailabilityIfNeeded]);

  const shouldDisableMonth = useCallback((monthDate) => {
    const sets = availabilityRef.current.sets || buildAvailabilityIfNeeded();
    const y = monthDate.getFullYear();
    const m = pad2(monthDate.getMonth() + 1);
    return !sets.availableMonths.has(`${y}-${m}`);
  }, [buildAvailabilityIfNeeded]);

  const shouldDisableDate = useCallback((dt) => {
    const sets = availabilityRef.current.sets || buildAvailabilityIfNeeded();
    const key = dateToDayKey(dt);
    return !key || !sets.availableDays.has(key);
  }, [buildAvailabilityIfNeeded]);

  const latestAvailableDate = getLatestAvailableDate(dataRef?.current);
  const calendarValue = selectedDate || latestAvailableDate;

  return (
    <ThemeProvider theme={darkTheme}>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", bgcolor: "background.paper", p: 1, borderRadius: 2, boxShadow: 24, minWidth: 300, maxWidth: 420 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={calendarWrapperSx}>
              <DateCalendar
                disableFuture
                views={["year", "month", "day"]}
                value={calendarValue}
                onChange={onCalendarChange}
                referenceDate={latestAvailableDate || undefined}
                displayWeekNumber={false}
                shouldDisableYear={shouldDisableYear}
                shouldDisableMonth={shouldDisableMonth}
                shouldDisableDate={shouldDisableDate}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Modal>
    </ThemeProvider>
  );
}
